import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { GlofoxBrandLogo } from "@/components/portal/GlofoxBrandLogo";
import { SkipToContent } from "@/components/portal/SkipToContent";
import { UserAccountMenu } from "@/components/portal/UserAccountMenu";
import { env } from "@/config/env";
import { useBrandHomeRoute } from "@/hooks/useBrandHomeRoute";
import { useColorTheme } from "@/hooks/useColorTheme";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  ACCESS_SCOPE_OPTIONS,
  getPlatformRequests,
  validateScopeText,
  type AccessScopeType,
} from "@/lib/api/access-request-form";
import {
  contestAccessRequest,
  createIntegratorRequest,
  listAccessRequests,
  requestLiveCredentials,
  type AccessRequest,
  type ApiPlatform,
  type KeyEnvironment,
} from "@/lib/api/access-requests";
import { parseApiError } from "@/lib/api/api-error";
import {
  LIVE_CREDENTIALS_ERROR_MESSAGES,
  LIVE_CREDENTIALS_FAILURE,
  PORTAL_LIST_FAILURE,
  presentApiError,
  SANDBOX_CREDENTIALS_FAILURE,
  SANDBOX_ERROR_MESSAGES,
  TEST_LOCATION_FAILURE,
  TEST_LOCATION_STILL_PROVISIONING,
  type PresentedApiError,
} from "@/lib/api/portal-error-copy";
import {
  storedLocationSelections,
  storedNamespaceSelections,
} from "@/lib/api/live-catalog";
import {
  CONTESTATION_MESSAGE_LIMITS,
  createContestFormFromRequest,
  getContestAccessRequestSubmitErrorMessage,
  isContestAccessRequestFormValid,
  toContestAccessRequestPayload,
  type ContestAccessRequestFormValues,
} from "@/lib/api/contest-access-request-form";
import {
  ACCESS_ROLE_OPTIONS,
  type AccessRole,
} from "@/lib/api/portal-access-role";
import {
  DEFAULT_STUDIO_TYPE,
  findWorkspaceForAccessRequest,
  listSandboxWorkspaces,
  requestSandboxCredentials,
} from "@/lib/api/glofox/sandbox-workspaces";
import {
  spinUpTestLocation,
  type TestLocationProvisionStatus,
} from "@/lib/api/glofox/spin-up-test-location";
import {
  createInitialIntegratorRequestForm,
  getIntegratorRequestSubmitErrorMessage,
  getIntegratorScopeText,
  INTEGRATOR_ENVIRONMENT_OPTIONS,
  isIntegratorRequestFormValid,
  toCreateIntegratorRequestPayload,
  type IntegratorRequestFormValues,
} from "@/lib/api/integrator-request-form";
import type { AuthSession } from "@/lib/auth/session";
import { updateAuthSessionUser } from "@/lib/auth/session";
import {
  useImportedCredentialsSupportEnabled,
  useImportedSandboxCredentialsEnabled,
  useLiveCredentialsEnabled,
} from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import {
  getGlofoxDocsHomeHref,
  GLOFOX_DOCS_HOME_ROUTE,
} from "@/lib/portal/docs-urls";
import { SAME_ORIGIN_NEW_TAB_REL } from "@/lib/portal/same-origin-new-tab";
import {
  applyLocationProvisionResult,
  attachSandboxLocationsToRequests,
  canRequestLiveCredentials,
  canRequestSandboxCredentials,
  credentialsActionKind,
  credentialsActionLabels,
  demoLocationDisplayName,
  displayedGlofoxIntegratorName,
  FALLBACK_BUSINESS_NAME,
  formatPortalRequestDate,
  glofoxIntegratorDisplayName,
  hasHeldIntegrator,
  isImportedFromGlofox,
  mapAccessRequestToIntegratorRequest,
  MAX_SANDBOX_TEST_LOCATIONS,
  nextSandboxLocationNumber,
  preserveClientPortalState,
  type Environment,
  type IntegratorRequest,
  type RequestStatus,
} from "@/pages/glofox/developer-portal-requests";
import { LiveScopePicker } from "@/pages/glofox/LiveScopePicker";
import "@/styles/developer-portal.css";

type ModalName = "request" | "location" | "contest" | null;

interface DeveloperPortalPageProps {
  session: AuthSession;
}

function StatusBadge({
  value,
}: {
  value: Environment | RequestStatus;
}) {
  return (
    <span
      className={`dev-status dev-status--${value.toLowerCase()}`}
      data-testid={`developer-portal-status-${value.toLowerCase()}`}
    >
      <span aria-hidden />
      {value}
    </span>
  );
}

type PortalToast = {
  message: string;
  code?: string;
  tone: "success" | "error" | "info";
};

export default function DeveloperPortalPage({
  session,
}: DeveloperPortalPageProps) {
  usePageTitle("API access");
  const liveCredentialsEnabled = useLiveCredentialsEnabled();
  const importedSandboxCredentialsEnabled =
    useImportedSandboxCredentialsEnabled();
  const businessName = session.user.company_name?.trim() || FALLBACK_BUSINESS_NAME;
  const apiPlatform = env.platform as ApiPlatform;
  const [requests, setRequests] = useState<IntegratorRequest[]>([]);
  const [accessRole, setAccessRole] = useState<AccessRole | null>(
    session.user.access_role ?? null,
  );
  const [listLoading, setListLoading] = useState(true);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [listError, setListError] = useState<PresentedApiError | null>(null);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const hasLoadedOnceRef = useRef(false);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalName>(null);
  const [locationSubmitting, setLocationSubmitting] = useState(false);
  const locationSubmitLockRef = useRef(false);
  const [credentialsSubmitting, setCredentialsSubmitting] = useState(false);
  const credentialsSubmitLockRef = useRef(false);
  const [toast, setToast] = useState<PortalToast | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const brandHomeRoute = useBrandHomeRoute();
  const { theme, setTheme } = useColorTheme();
  const listBusy = listLoading || listRefreshing;

  useEffect(() => {
    let cancelled = false;

    async function loadRequests() {
      const isRefresh = hasLoadedOnceRef.current;
      if (isRefresh) {
        setListRefreshing(true);
      } else {
        setListLoading(true);
      }
      setListError(null);
      setWorkspaceError(null);
      try {
        const [{ requests: backendRequests }, workspacesResult] =
          await Promise.all([
            listAccessRequests(),
            listSandboxWorkspaces().then(
              (result) => ({ ok: true as const, result }),
              () => ({ ok: false as const }),
            ),
          ]);
        if (cancelled) {
          return;
        }
        const mapped = getPlatformRequests(backendRequests, apiPlatform)
          .slice()
          .sort(
            (left, right) =>
              new Date(right.created_at).getTime() -
              new Date(left.created_at).getTime(),
          )
          .map((request) =>
            mapAccessRequestToIntegratorRequest(request, businessName),
          );
        const withLocations = workspacesResult.ok
          ? attachSandboxLocationsToRequests(
              mapped,
              workspacesResult.result.workspaces,
            )
          : mapped;
        if (!workspacesResult.ok) {
          setWorkspaceError(
            "Sandbox workspace details could not be refreshed. Existing details may be out of date.",
          );
        }
        setRequests((current) => {
          const localOnly = current.filter((request) =>
            request.id.startsWith("local-"),
          );
          return preserveClientPortalState(
            [...localOnly, ...withLocations],
            current,
          );
        });
        if (isRefresh) {
          notify({
            message: "Integrator requests updated.",
            tone: "success",
          });
        }
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }
        setListError(
          presentApiError(err, { generic: PORTAL_LIST_FAILURE }),
        );
        setRequests((current) => {
          const localOnly = current.filter((request) =>
            request.id.startsWith("local-"),
          );
          return preserveClientPortalState(localOnly, current);
        });
      } finally {
        if (!cancelled) {
          hasLoadedOnceRef.current = true;
          setListLoading(false);
          setListRefreshing(false);
        }
      }
    }

    void loadRequests();

    return () => {
      cancelled = true;
    };
  }, [apiPlatform, businessName, reloadKey]);

  const selected = requests.find((request) => request.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected || modal) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      setSelectedId(null);
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [selected, modal]);

  const filteredRequests = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return requests;
    return requests.filter((request) =>
      [
        request.name,
        request.business,
        request.environment,
        request.status,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    );
  }, [query, requests]);

  const hasSandboxRequest = requests.some(
    (request) => request.environment === "Sandbox",
  );
  const hasLiveRequest = requests.some(
    (request) => request.environment === "Live",
  );
  const allEnvironmentsRequested = hasSandboxRequest && hasLiveRequest;

  useEffect(() => {
    return () => {
      if (toastTimerRef.current != null) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  function dismissToast() {
    if (toastTimerRef.current != null) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  }

  function notify(next: PortalToast) {
    if (toastTimerRef.current != null) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(next);
    if (next.tone === "error") {
      return;
    }
    const delay = next.tone === "info" ? 8000 : 3200;
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, delay);
  }

  function handleRequestCreated(created: AccessRequest) {
    if (!accessRole) {
      setAccessRole(created.requester_type);
      updateAuthSessionUser({
        ...session.user,
        access_role: created.requester_type,
      });
    }
    const request = mapAccessRequestToIntegratorRequest(created, businessName);
    setRequests((current) => {
      const others = current.filter((item) => item.id !== request.id);
      return [request, ...others];
    });
    setSelectedId(request.id);
    setModal(null);
    notify({
      message: "Integrator request submitted and pending review.",
      tone: "success",
    });
  }

  function handleRequestContested(updated: AccessRequest) {
    const request = mapAccessRequestToIntegratorRequest(updated, businessName);
    setRequests((current) => {
      const others = current.filter((item) => item.id !== request.id);
      return preserveClientPortalState([request, ...others], current);
    });
    setSelectedId(request.id);
    setModal(null);
    notify({
      message: "Contest submitted. Your request is back in review.",
      tone: "success",
    });
  }

  async function submitLocation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (locationSubmitLockRef.current || locationSubmitting) {
      return;
    }

    const data = new FormData(event.currentTarget);
    const adminPassword = String(data.get("adminPassword") ?? "");
    if (!adminPassword || adminPassword.length < 8 || !selected) {
      return;
    }
    if (selected.status !== "Approved" || selected.environment !== "Sandbox") {
      return;
    }
    if (
      countSuccessfulLocations(selected) >= MAX_SANDBOX_TEST_LOCATIONS ||
      selected.locations.some((location) => location.status === "pending")
    ) {
      return;
    }

    locationSubmitLockRef.current = true;
    setLocationSubmitting(true);

    const requestId = selected.id;
    const locationNumber = nextSandboxLocationNumber(selected.locations);
    const locationName = demoLocationDisplayName(
      selected.business,
      selected.name,
      locationNumber,
    );
    const pendingId = `pending_${String(Date.now())}`;

    setModal(null);
    setRequests((current) => {
      const request = current.find((item) => item.id === requestId);
      if (
        !request ||
        countSuccessfulLocations(request) >= MAX_SANDBOX_TEST_LOCATIONS ||
        request.locations.some((location) => location.status === "pending")
      ) {
        return current;
      }

      return current.map((item) =>
        item.id === requestId
          ? {
              ...item,
              locations: [
                ...item.locations,
                {
                  id: pendingId,
                  name: locationName,
                  status: "pending" as const,
                },
              ],
              activity: [
                {
                  label: "Test location provisioning started",
                  date: formatPortalRequestDate(new Date().toISOString()),
                },
                ...item.activity,
              ],
            }
          : item,
      );
    });
    notify({ message: "Creating sandbox test location…", tone: "info" });

    try {
      const result = await spinUpTestLocation({
        accessRequestId: requestId,
        locationName,
        adminPassword,
        studioType: DEFAULT_STUDIO_TYPE,
      });

      setRequests((current) =>
        applyLocationProvisionResult(current, requestId, pendingId, result),
      );

      if (result.status === "success") {
        notify({ message: "Sandbox test location created.", tone: "success" });
      } else if (result.status === "pending") {
        notify({
          message: result.statusMessage ?? TEST_LOCATION_STILL_PROVISIONING,
          tone: "info",
        });
      } else {
        notify({
          message: result.errorMessage ?? TEST_LOCATION_FAILURE,
          code: result.errorCode,
          tone: "error",
        });
      }
    } catch {
      setRequests((current) =>
        applyLocationProvisionResult(current, requestId, pendingId, {
          id: pendingId,
          name: locationName,
          status: "error",
          errorMessage: TEST_LOCATION_FAILURE,
        }),
      );
      notify({ message: TEST_LOCATION_FAILURE, tone: "error" });
    } finally {
      locationSubmitLockRef.current = false;
      setLocationSubmitting(false);
    }
  }

  async function submitCredentials() {
    if (!selected || credentialsSubmitLockRef.current || credentialsSubmitting) {
      return;
    }
    if (selected.status !== "Approved") {
      return;
    }
    if (selected.environment === "Live") {
      await submitLiveCredentials(selected);
      return;
    }
    if (selected.environment !== "Sandbox") {
      return;
    }
    if (
      !canRequestSandboxCredentials(selected, {
        importedSandboxCredentialsEnabled,
      })
    ) {
      return;
    }

    credentialsSubmitLockRef.current = true;
    setCredentialsSubmitting(true);

    const actionKind = credentialsActionKind(selected.credentialsStatus);
    const requestId = selected.id;
    let workspaceId = selected.sandboxWorkspaceId;

    try {
      if (!workspaceId) {
        const { workspaces } = await listSandboxWorkspaces();
        workspaceId = findWorkspaceForAccessRequest(workspaces, requestId)?.id;
      }
      if (!workspaceId) {
        const message = SANDBOX_ERROR_MESSAGES["sandbox.workspace_not_found"];
        notify({
          message,
          code: "sandbox.workspace_not_found",
          tone: "error",
        });
        setRequests((current) =>
          current.map((request) =>
            request.id === requestId
              ? {
                  ...request,
                  credentialsErrorMessage: message,
                  credentialsErrorCode: "sandbox.workspace_not_found",
                }
              : request,
          ),
        );
        return;
      }

      const result = await requestSandboxCredentials(workspaceId);
      const nextStatus = result.status ?? "issued";
      setRequests((current) =>
        current.map((request) =>
          request.id === requestId
            ? {
                ...request,
                sandboxWorkspaceId: workspaceId,
                credentialsStatus: nextStatus,
                credentialsRequested: true,
                credentialsErrorMessage: undefined,
                credentialsErrorCode: undefined,
                activity: [
                  {
                    label:
                      actionKind === "resend"
                        ? "API credentials resent"
                        : "API credential request submitted",
                    date: formatPortalRequestDate(new Date().toISOString()),
                  },
                  ...request.activity,
                ],
              }
            : request,
        ),
      );
      notify({
        message:
          result.message?.trim() ||
          (actionKind === "resend"
            ? "API credentials resent."
            : "API credential request submitted."),
        tone: "success",
      });
    } catch (err: unknown) {
      const parsed = parseApiError(err);
      const presented = presentApiError(err, {
        generic: SANDBOX_CREDENTIALS_FAILURE,
        byCode: SANDBOX_ERROR_MESSAGES,
      });
      notify({
        message: presented.message,
        code: presented.code,
        tone: "error",
      });
      const persistFailed =
        parsed.isNetworkError ||
        parsed.status == null ||
        parsed.status >= 500;
      setRequests((current) =>
        current.map((request) =>
          request.id === requestId
            ? {
                ...request,
                sandboxWorkspaceId: workspaceId,
                ...(persistFailed
                  ? {
                      credentialsStatus: "failed" as const,
                      credentialsRequested: true,
                    }
                  : {}),
                credentialsErrorMessage: presented.message,
                credentialsErrorCode: presented.code,
              }
            : request,
        ),
      );
    } finally {
      credentialsSubmitLockRef.current = false;
      setCredentialsSubmitting(false);
    }
  }

  async function submitLiveCredentials(request: IntegratorRequest) {
    if (!canRequestLiveCredentials(request, { liveCredentialsEnabled })) {
      return;
    }

    credentialsSubmitLockRef.current = true;
    setCredentialsSubmitting(true);

    const actionKind = credentialsActionKind(request.credentialsStatus);
    const requestId = request.id;

    try {
      const result = await requestLiveCredentials(requestId);
      const nextStatus = result.status ?? "issued";
      setRequests((current) =>
        current.map((row) =>
          row.id === requestId
            ? {
                ...row,
                credentialsStatus: nextStatus,
                credentialsRequested: true,
                credentialsErrorMessage: undefined,
                credentialsErrorCode: undefined,
                activity: [
                  {
                    label:
                      actionKind === "resend"
                        ? "API credentials resent"
                        : "API credential request submitted",
                    date: formatPortalRequestDate(new Date().toISOString()),
                  },
                  ...row.activity,
                ],
              }
            : row,
        ),
      );
      notify({
        message:
          result.message?.trim() ||
          (actionKind === "resend"
            ? "API credentials resent."
            : "API credential request submitted."),
        tone: "success",
      });
    } catch (err: unknown) {
      const parsed = parseApiError(err);
      const presented = presentApiError(err, {
        generic: LIVE_CREDENTIALS_FAILURE,
        byCode: LIVE_CREDENTIALS_ERROR_MESSAGES,
      });
      notify({
        message: presented.message,
        code: presented.code,
        tone: "error",
      });
      const persistFailed =
        parsed.isNetworkError || parsed.status == null || parsed.status >= 500;
      setRequests((current) =>
        current.map((row) =>
          row.id === requestId
            ? {
                ...row,
                ...(persistFailed
                  ? {
                      credentialsStatus: "failed" as const,
                      credentialsRequested: true,
                    }
                  : {}),
                credentialsErrorMessage: presented.message,
                credentialsErrorCode: presented.code,
              }
            : row,
        ),
      );
    } finally {
      credentialsSubmitLockRef.current = false;
      setCredentialsSubmitting(false);
    }
  }

  return (
    <div className="dev-portal">
      <SkipToContent />
      <header className="dev-topbar">
        <div className="dev-topbar__brand">
          <AbcFitnessBrandLogo
            to={brandHomeRoute}
            theme="dark"
            testId="developer-portal-abc-logo"
          />
          <span className="dev-topbar__brand-separator" aria-hidden>
            /
          </span>
          <GlofoxBrandLogo
            to={GLOFOX_DOCS_HOME_ROUTE}
            testId="developer-portal-logo"
          />
        </div>
        <nav className="dev-topbar__nav" aria-label="Developer Portal">
          <span className="is-active">Developer Portal</span>
        </nav>
        <div className="dev-topbar__actions">
          <label className="dev-theme-toggle" title="Toggle color theme">
            <input
              type="checkbox"
              role="switch"
              checked={theme === "dark"}
              aria-label="Dark mode"
              onChange={(event) =>
                setTheme(event.target.checked ? "dark" : "light")
              }
            />
            <span aria-hidden>
              <i
                className={`ti ${
                  theme === "dark" ? "ti-sun" : "ti-moon"
                }`}
              />
            </span>
          </label>
          <a
            className="dev-button dev-button--docs"
            href={getGlofoxDocsHomeHref()}
            target="_blank"
            rel={SAME_ORIGIN_NEW_TAB_REL}
            data-testid="developer-portal-docs-link"
          >
            <i className="ti ti-book-2" aria-hidden />
            Back to documentation
            <i className="ti ti-external-link" aria-hidden />
          </a>
          <UserAccountMenu
            userName={session.user.contact_name}
            testIdPrefix="developer-portal"
            variant="portal"
          />
        </div>
      </header>

      <main
        id="main-content"
        data-testid="main-content"
        className={`dev-main${selected ? " dev-main--panel-open" : ""}`}
      >
        <div className="dev-content">
          <div className="dev-page-header">
            <div>
              <h1 data-testid="developer-portal-page-title">API access</h1>
              <p>
                Request and manage the integrators connected to your Glofox
                account. Available actions depend on the environment and
                approval status.
              </p>
            </div>
            <LockableRequestIntegratorButton
              locked={allEnvironmentsRequested}
              onClick={() => setModal("request")}
            />
          </div>

          <section className="dev-table-card" aria-labelledby="integrators-title">
            <h2 id="integrators-title" className="sr-only">
              Integrator requests
            </h2>
            <div className="dev-toolbar">
              <label className="dev-search">
                <span className="sr-only">Search integrator requests</span>
                <i className="ti ti-search" aria-hidden />
                <input
                  type="search"
                  data-testid="developer-portal-search"
                  value={query}
                  placeholder="Search by integrator, business, environment, or status"
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
              <button
                type="button"
                className={`dev-toolbar-refresh${listBusy ? " is-busy" : ""}`}
                aria-label="Refresh integrator requests"
                data-testid="developer-portal-refresh"
                disabled={listBusy}
                onClick={() => setReloadKey((key) => key + 1)}
              >
                <i
                  className={`ti ${listBusy ? "ti-loader-2" : "ti-refresh"}`}
                  aria-hidden
                />
                {listRefreshing ? "Updating" : "Refresh"}
              </button>
              <span
                data-testid="developer-portal-search-summary"
                aria-live="polite"
              >
                {listLoading
                  ? "Loading requests…"
                  : `Showing ${filteredRequests.length} of ${requests.length}`}
              </span>
            </div>
            {listError ? (
              <div className="dev-banner dev-banner--declined" role="alert">
                <i className="ti ti-alert-circle" aria-hidden />
                <span>
                  <strong>Could not load your requests.</strong>{" "}
                  {listError.message}
                  {listError.code ? (
                    <span className="api-error-code">{listError.code}</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="dev-banner__retry"
                  data-testid="developer-portal-retry"
                  disabled={listBusy}
                  onClick={() => setReloadKey((key) => key + 1)}
                >
                  Retry
                </button>
              </div>
            ) : null}
            {workspaceError ? (
              <div className="dev-banner dev-banner--declined" role="alert">
                <i className="ti ti-alert-circle" aria-hidden />
                <span>
                  <strong>Some sandbox details are unavailable.</strong>{" "}
                  {workspaceError}
                </span>
                <button
                  type="button"
                  className="dev-banner__retry"
                  onClick={() => setReloadKey((key) => key + 1)}
                >
                  Retry
                </button>
              </div>
            ) : null}
            <div className="dev-table-head" aria-hidden>
              <span>Integrator</span>
              <span>Business</span>
              <span>Environment</span>
              <span>Status</span>
              <span>Requested on</span>
              <span />
            </div>
            <div
              className={`dev-table-body${listRefreshing ? " is-refreshing" : ""}`}
              data-testid="developer-portal-request-list"
              aria-busy={listRefreshing}
            >
              {listRefreshing ? (
                <div
                  className="dev-table-refresh"
                  role="status"
                  data-testid="developer-portal-list-refreshing"
                >
                  <i className="ti ti-loader-2" aria-hidden />
                  Updating requests…
                </div>
              ) : null}
              {filteredRequests.length ? (
                filteredRequests.map((request) => (
                  <div key={request.id}>
                    <button
                      className={`dev-table-row${
                        request.id === selectedId ? " is-selected" : ""
                      }`}
                      type="button"
                      data-testid={`developer-portal-request-${request.id}`}
                      data-environment={request.environment}
                      data-status={request.status}
                      data-name={request.name}
                      onClick={() => setSelectedId(request.id)}
                      aria-label={`View ${request.name}`}
                    >
                      <span className="dev-table-row__integrator">
                        <strong data-testid="developer-portal-request-row-name">
                          {request.name}
                        </strong>
                      </span>
                      <span>{request.business}</span>
                      <span>
                        <StatusBadge value={request.environment} />
                      </span>
                      <span>
                        <StatusBadge value={request.status} />
                      </span>
                      <span>{request.requested}</span>
                      <i className="ti ti-chevron-right" aria-hidden />
                    </button>
                  </div>
                ))
              ) : (
                <div
                  className="dev-empty"
                  data-testid="developer-portal-search-empty"
                >
                  <i className="ti ti-search-off" aria-hidden />
                  <p>
                    {listLoading
                      ? "Loading your access requests…"
                      : listError
                        ? listError.message
                        : query.trim()
                          ? "No integrators match your search."
                          : "No integrator requests yet."}
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {selected ? (
        <aside
          className="dev-detail-panel"
          aria-label={`${selected.name} details`}
          data-testid="developer-portal-detail-panel"
        >
          <div className="dev-detail-panel__header">
            <button
              className="dev-icon-button"
              type="button"
              aria-label="Close integrator details"
              onClick={() => setSelectedId(null)}
            >
              <i className="ti ti-x" aria-hidden />
            </button>
            <span>Integrator request</span>
            <h2 data-testid="developer-portal-detail-name">{selected.name}</h2>
            <div className="dev-detail-panel__badges">
              <StatusBadge value={selected.environment} />
              <StatusBadge value={selected.status} />
            </div>
          </div>
          <div className="dev-detail-panel__body">
            <RequestJourney status={selected.status} />
            <section className="dev-panel-section">
              <h3>Request details</h3>
              <dl className="dev-detail-grid">
                <div>
                  <dt>Business</dt>
                  <dd>{selected.business}</dd>
                </div>
                <div>
                  <dt>Environment</dt>
                  <dd>{selected.environment}</dd>
                </div>
                <div className="dev-detail-grid__full">
                  <dt>Glofox integrator name</dt>
                  <dd data-testid="developer-portal-detail-glofox-name">
                    <code>
                      {displayedGlofoxIntegratorName(selected)}
                    </code>
                  </dd>
                </div>
                <div>
                  <dt>Access scope</dt>
                  <dd data-testid="developer-portal-detail-access-scope">
                    {selected.scope}
                  </dd>
                </div>
                <div>
                  <dt>Requested on</dt>
                  <dd>{selected.requested}</dd>
                </div>
                <div className="dev-detail-grid__full">
                  <dt>Intended use</dt>
                  <dd data-testid="developer-portal-detail-intended-use">
                    {selected.useCase}
                  </dd>
                </div>
              </dl>
            </section>
            <RequestActions
              request={selected}
              credentialsSubmitting={credentialsSubmitting}
              onCreateLocation={() => setModal("location")}
              onContest={() => setModal("contest")}
              onCredentials={() => {
                void submitCredentials();
              }}
            />
            <section
              className="dev-panel-section"
              data-testid="developer-portal-activity"
            >
              <h3>Request activity</h3>
              <ol className="dev-activity">
                {selected.activity.map((activity, index) => (
                  <li
                    key={`${activity.label}-${activity.date}-${index}`}
                    data-testid={`developer-portal-activity-item-${index}`}
                  >
                    <strong>{activity.label}</strong>
                    <span>{activity.date}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </aside>
      ) : null}

      {modal === "request" ? (
        <PortalModal
          title="Request integrator"
          description="Create a new integrator request. Live requests require an access scope."
          testId="developer-portal-request-modal"
          onClose={() => setModal(null)}
        >
          <RequestIntegratorForm
            apiPlatform={apiPlatform}
            accessRole={accessRole}
            businessName={businessName}
            hasSandboxRequest={hasSandboxRequest}
            hasLiveRequest={hasLiveRequest}
            onCancel={() => setModal(null)}
            onCreated={handleRequestCreated}
          />
        </PortalModal>
      ) : null}

      {modal === "contest" && selected?.sourceRequest ? (
        <PortalModal
          title="Contest this decision"
          description="Update your request details and explain what changed. You can contest a declined request once."
          testId="developer-portal-contest-modal"
          onClose={() => setModal(null)}
        >
          <ContestAccessRequestForm
            request={selected.sourceRequest}
            businessName={businessName}
            onCancel={() => setModal(null)}
            onContested={handleRequestContested}
          />
        </PortalModal>
      ) : null}

      {modal === "location" ? (
        <PortalModal
          title="Create test location"
          description="Create an isolated sandbox location for integration testing."
          testId="developer-portal-location-modal"
          onClose={() => setModal(null)}
        >
          <form
            className="dev-form"
            data-testid="developer-portal-location-form"
            onSubmit={(event) => {
              void submitLocation(event);
            }}
          >
            <ZapierApiKeyCallout testId="developer-portal-location-zapier-info" />
            <div
              className="dev-form__info"
              data-testid="developer-portal-location-credentials-info"
            >
              <i className="ti ti-info-circle" aria-hidden />
              Create a password for the test location dashboard. This password
              is only for the test location — it does not change your
              Developer Portal password. Log in to the dashboard with your
              portal email and this password.
            </div>
            <label>
              <span>Test location password</span>
              <input
                name="adminPassword"
                type="password"
                data-testid="developer-portal-location-password"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            <ModalActions
              onCancel={() => setModal(null)}
              submit="Create location"
              submitting={locationSubmitting}
              submitTestId="developer-portal-location-submit"
              cancelTestId="developer-portal-location-cancel"
            />
          </form>
        </PortalModal>
      ) : null}

      {toast ? (
        <div
          className={`dev-toast dev-toast--${toast.tone}`}
          role={toast.tone === "error" ? "alert" : "status"}
          data-testid="developer-portal-toast"
        >
          <i
            className={`ti ${
              toast.tone === "error"
                ? "ti-alert-circle"
                : toast.tone === "info"
                  ? "ti-info-circle"
                  : "ti-circle-check"
            }`}
            aria-hidden
          />
          <span className="dev-toast__body">
            <span>{toast.message}</span>
            {toast.code ? (
              <span className="dev-toast__code">{toast.code}</span>
            ) : null}
          </span>
          {toast.tone === "error" ? (
            <button
              type="button"
              className="dev-toast__dismiss"
              data-testid="developer-portal-toast-dismiss"
              onClick={dismissToast}
            >
              Dismiss
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Shown when the user holds both integrator requests the portal allows. */
const REQUEST_LIMIT_TOOLTIP =
  "You can create only two integrators: one sandbox and one live.";

const ENVIRONMENT_ALREADY_REQUESTED_TOOLTIP: Record<KeyEnvironment, string> = {
  sandbox:
    "You already have a Sandbox integrator. You can create only one Sandbox and one Live integrator.",
  live: "You already have a Live integrator. You can create only one Sandbox and one Live integrator.",
};

function LockableRequestIntegratorButton({
  locked,
  onClick,
}: {
  locked: boolean;
  onClick(): void;
}) {
  const tooltipId = useId();

  return (
    <span className="dev-tooltip-anchor">
      <button
        className={`dev-button dev-button--primary${locked ? " is-locked" : ""}`}
        type="button"
        data-testid="developer-portal-request-integrator"
        onClick={onClick}
        disabled={locked}
        aria-describedby={locked ? tooltipId : undefined}
      >
        <i className="ti ti-plus" aria-hidden />
        Request integrator
      </button>
      {locked ? (
        <span
          className="dev-tooltip"
          id={tooltipId}
          role="tooltip"
          data-testid="developer-portal-request-integrator-tooltip"
        >
          {REQUEST_LIMIT_TOOLTIP}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Environment toggle that explains why Sandbox or Live is unavailable when the
 * user already holds that environment.
 */
function EnvironmentToggleButton({
  value,
  label,
  pressed,
  unavailable,
  tooltipAlignStart,
  onSelect,
}: {
  value: KeyEnvironment;
  label: string;
  pressed: boolean;
  unavailable: boolean;
  tooltipAlignStart?: boolean;
  onSelect(): void;
}) {
  const tooltipId = useId();
  const [showTooltip, setShowTooltip] = useState(false);

  function handleClick() {
    if (unavailable) {
      setShowTooltip(true);
      return;
    }
    setShowTooltip(false);
    onSelect();
  }

  return (
    <span
      className="dev-tooltip-anchor"
      onPointerLeave={() => {
        if (unavailable) {
          setShowTooltip(false);
        }
      }}
    >
      <button
        className={`${pressed ? "is-active" : ""}${unavailable ? " is-locked" : ""}`.trim()}
        type="button"
        data-testid={`developer-portal-request-env-${value}`}
        aria-pressed={pressed}
        aria-disabled={unavailable || undefined}
        aria-describedby={unavailable ? tooltipId : undefined}
        onClick={handleClick}
        onBlur={() => {
          if (unavailable) {
            setShowTooltip(false);
          }
        }}
      >
        {label}
      </button>
      {unavailable ? (
        <span
          className={`dev-tooltip${tooltipAlignStart ? " dev-tooltip--start" : ""}${showTooltip ? " is-visible" : ""}`}
          id={tooltipId}
          role="tooltip"
          data-testid={`developer-portal-request-env-${value}-tooltip`}
        >
          {ENVIRONMENT_ALREADY_REQUESTED_TOOLTIP[value]}
        </span>
      ) : null}
    </span>
  );
}

function RequestJourney({ status }: { status: RequestStatus }) {
  const finalLabel = status === "Declined" ? "Declined" : "Approved";
  return (
    <ol className="dev-journey" aria-label="Request progress">
      {["Submitted", "Review", finalLabel].map((label, index) => {
        const complete =
          index === 0 ||
          (index === 1 && status !== "Pending") ||
          (index === 2 && status === "Approved");
        const current =
          (index === 1 && status === "Pending") ||
          (index === 2 && status === "Declined");
        return (
          <li
            key={label}
            className={`${complete ? "is-complete" : ""}${
              current ? " is-current" : ""
            }`}
          >
            <span>{complete ? <i className="ti ti-check" aria-hidden /> : index + 1}</span>
            <small>{label}</small>
          </li>
        );
      })}
    </ol>
  );
}

function CredentialsErrorBanner({ request }: { request: IntegratorRequest }) {
  if (!request.credentialsErrorMessage) {
    return null;
  }

  return (
    <div
      className="dev-banner dev-banner--declined"
      role="alert"
      data-testid="developer-portal-credentials-error"
    >
      <i className="ti ti-alert-circle" aria-hidden />
      <span>
        {request.credentialsErrorMessage}
        {request.credentialsErrorCode ? (
          <span className="api-error-code">{request.credentialsErrorCode}</span>
        ) : null}
      </span>
    </div>
  );
}

export function RequestActions({
  request,
  credentialsSubmitting = false,
  onCreateLocation,
  onCredentials,
  onContest,
}: {
  request: IntegratorRequest;
  credentialsSubmitting?: boolean;
  onCreateLocation(): void;
  onCredentials(): void;
  onContest?(): void;
}) {
  const liveCredentialsEnabled = useLiveCredentialsEnabled();
  const importedSandboxCredentialsEnabled =
    useImportedSandboxCredentialsEnabled();
  const importedCredentialsSupportEnabled =
    useImportedCredentialsSupportEnabled();

  if (request.status === "Pending") {
    return (
      <div className="dev-banner dev-banner--pending">
        <i className="ti ti-clock" aria-hidden />
        <span>
          <strong>Awaiting review.</strong> Actions become available after
          approval.
        </span>
      </div>
    );
  }
  if (request.status === "Declined") {
    return (
      <div className="dev-panel-section" data-testid="developer-portal-declined">
        <div className="dev-banner dev-banner--declined">
          <i className="ti ti-circle-x" aria-hidden />
          <span>
            <strong>Request declined.</strong>{" "}
            {request.declineReason ??
              "Contact Glofox API support for details."}
          </span>
        </div>
        {request.canContest && onContest ? (
          <div className="dev-action-grid">
            <ActionCard
              icon="ti-message-reply"
              title="Contest this decision"
              action="Contest this decision"
              disabled={false}
              onClick={onContest}
              testId="developer-portal-action-contest"
            />
          </div>
        ) : (
          <p
            className="dev-panel-hint"
            data-testid="developer-portal-contest-unavailable"
          >
            This request can no longer be contested. Contact Glofox API support
            if you need help.
          </p>
        )}
      </div>
    );
  }

  if (request.environment === "Live") {
    if (request.fulfillment === "revoked") {
      return (
        <div
          className="dev-banner dev-banner--declined"
          data-testid="developer-portal-live-revoked"
        >
          <i className="ti ti-circle-x" aria-hidden />
          <span>
            <strong>Live access revoked.</strong> The granted locations or
            namespaces were removed. API credentials are no longer available,
            and this scope cannot be edited.
          </span>
        </div>
      );
    }

    if (
      canRequestLiveCredentials(request, { liveCredentialsEnabled })
    ) {
      const credentialLabels = credentialsActionLabels(request.credentialsStatus);
      return (
        <>
          <div className="dev-banner dev-banner--approved">
            <i className="ti ti-circle-check" aria-hidden />
            <span>
              <strong>Integrator approved.</strong> The actions below are
              available.
            </span>
          </div>
          <section
            className="dev-panel-section"
            data-testid="developer-portal-available-actions"
          >
            <h3>Available actions</h3>
            <CredentialsErrorBanner request={request} />
            <div className="dev-action-grid">
              <ActionCard
                icon="ti-key"
                title={credentialLabels.title}
                action={
                  credentialsSubmitting
                    ? "Requesting…"
                    : credentialLabels.action
                }
                disabled={credentialsSubmitting}
                onClick={onCredentials}
                testId="developer-portal-action-credentials"
              />
            </div>
          </section>
        </>
      );
    }

    return (
      <>
        <div className="dev-banner dev-banner--approved">
          <i className="ti ti-circle-check" aria-hidden />
          <span>
            <strong>Integrator approved.</strong> Live access was created for the
            approved scope. Live credentials are issued by the API Activation
            team — this portal does not email API keys. No further actions are
            available here.
          </span>
        </div>
        {importedCredentialsSupportEnabled &&
        showImportedLiveCredentialSupport(request) ? (
          <ImportedCredentialsSupportNote />
        ) : null}
      </>
    );
  }

  const successfulLocations = countSuccessfulLocations(request);
  const hasPendingLocation = request.locations.some(
    (location) => location.status === "pending",
  );
  const credentialsUnlocked = canRequestSandboxCredentials(request, {
    importedSandboxCredentialsEnabled,
  });
  const heldWithoutLocations =
    request.locations.length === 0 && hasHeldIntegrator(request);
  const locationLimitReached =
    successfulLocations >= MAX_SANDBOX_TEST_LOCATIONS;
  const createLocationDisabled = locationLimitReached || hasPendingLocation;
  const credentialLabels = credentialsActionLabels(request.credentialsStatus);
  const approvedBanner = (
    <div className="dev-banner dev-banner--approved">
      <i className="ti ti-circle-check" aria-hidden />
      <span>
        <strong>Integrator approved.</strong> The actions below are available.
      </span>
    </div>
  );

  return (
    <>
      {approvedBanner}
      <section
        className="dev-panel-section"
        data-testid="developer-portal-test-locations"
      >
        <h3>Test locations</h3>
        {request.locations.length ? (
          <>
            <div className="dev-location-list">
              {request.locations.map((location) => (
                <div
                  className={`dev-location dev-location--${location.status}`}
                  key={location.id}
                  data-testid={`dev-location-${location.status}`}
                >
                  <i className="ti ti-building-store" aria-hidden />
                  <span>
                    <strong>{location.name}</strong>
                    <small data-testid="developer-portal-test-location-id">
                      {location.status === "pending"
                        ? (location.statusMessage ?? "Provisioning…")
                        : `Branch ID: ${location.branchId ?? "Unavailable"}`}
                    </small>
                    {location.status === "error" && location.errorMessage ? (
                      <small className="dev-location__error">
                        {location.errorMessage}
                        {location.errorCode ? (
                          <span className="api-error-code">
                            {location.errorCode}
                          </span>
                        ) : null}
                      </small>
                    ) : null}
                    {location.status === "success" ? (
                      <a
                        className="dev-location__dashboard"
                        href={env.glofoxStudioDashboardUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="dev-location-dashboard-link"
                      >
                        Open location dashboard
                      </a>
                    ) : null}
                  </span>
                  <LocationProvisionBadge status={location.status} />
                </div>
              ))}
            </div>
            <p
              className="dev-panel-hint"
              data-testid="developer-portal-test-locations-dashboard-note"
            >
              Use your portal email and the test location password you entered
              to log in to the location dashboard.
            </p>
          </>
        ) : (
          <p
            className="dev-panel-hint"
            data-testid="developer-portal-test-locations-empty"
          >
            {isImportedFromGlofox(request.origin) &&
            importedSandboxCredentialsEnabled
              ? "Test locations will appear here later."
              : heldWithoutLocations
                ? "No test locations yet. Create one to exercise your sandbox API."
                : "Create a test location to unlock API credentials."}
          </p>
        )}
      </section>
      <section
        className="dev-panel-section"
        data-testid="developer-portal-available-actions"
      >
        <h3>Available actions</h3>
        {locationLimitReached ? (
          <div
            className="dev-banner dev-banner--location-hint"
            data-testid="developer-portal-location-limit-hint"
          >
            <i className="ti ti-info-circle" aria-hidden />
            <span>
              This sandbox already has two test locations.
            </span>
          </div>
        ) : null}
        <CredentialsErrorBanner request={request} />
        <div className="dev-action-grid">
          <ActionCard
            icon="ti-building-store"
            title="Create test location"
            action="Create location"
            disabled={createLocationDisabled}
            onClick={onCreateLocation}
            testId="developer-portal-action-create-location"
          />
          <ActionCard
              icon="ti-key"
              title={credentialLabels.title}
              action={
                credentialsSubmitting
                  ? "Requesting…"
                  : credentialLabels.action
              }
              disabled={!credentialsUnlocked || credentialsSubmitting}
              onClick={onCredentials}
              testId="developer-portal-action-credentials"
          />
        </div>
      </section>
    </>
  );
}

const IMPORTED_CREDENTIALS_SUPPORT_EMAIL = "Glofox.APISupport@abcfitness.com";

function showImportedLiveCredentialSupport(request: IntegratorRequest): boolean {
  return (
    request.status === "Approved" &&
    request.environment === "Live" &&
    isImportedFromGlofox(request.origin)
  );
}

function ImportedCredentialsSupportNote() {
  return (
    <div
      className="dev-banner dev-banner--support"
      role="note"
      data-testid="developer-portal-imported-credentials-support"
    >
      <i className="ti ti-mail" aria-hidden />
      <span>
        To have your credentials sent again, contact{" "}
        <a href={`mailto:${IMPORTED_CREDENTIALS_SUPPORT_EMAIL}`}>
          {IMPORTED_CREDENTIALS_SUPPORT_EMAIL}
        </a>
        .
      </span>
    </div>
  );
}

function countSuccessfulLocations(request: IntegratorRequest): number {
  return request.locations.filter((location) => location.status === "success")
    .length;
}

function LocationProvisionBadge({
  status,
}: {
  status: TestLocationProvisionStatus;
}) {
  const label =
    status === "success" ? "Ready" : status === "pending" ? "Pending" : "Failed";
  return (
    <span
      className={`dev-status dev-status--${status}`}
      data-testid={`dev-location-badge-${status}`}
    >
      <span aria-hidden />
      {label}
    </span>
  );
}

function ActionCard({
  icon,
  title,
  action,
  disabled,
  onClick,
  testId,
}: {
  icon: string;
  title: string;
  action: string;
  disabled: boolean;
  onClick(): void;
  testId?: string;
}) {
  return (
    <div
      className={`dev-action-card${disabled ? " is-disabled" : ""}`}
      data-testid={testId}
    >
      <i className={`ti ${icon}`} aria-hidden />
      <strong>{title}</strong>
      <button
        type="button"
        disabled={disabled}
        data-testid={testId ? `${testId}-button` : undefined}
        onClick={onClick}
      >
        {action}
      </button>
    </div>
  );
}

function PortalModal({
  title,
  description,
  onClose,
  children,
  testId,
}: {
  title: string;
  description: string;
  onClose(): void;
  children: React.ReactNode;
  testId?: string;
}) {
  const titleId = useId();
  const modalRef = useRef<HTMLElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useDialogFocusTrap(true, modalRef, onClose, closeBtnRef);

  return (
    <div className="dev-modal-root">
      <button
        type="button"
        className="dev-modal-backdrop"
        aria-label="Close dialog"
        data-testid={testId ? `${testId}-backdrop` : undefined}
        onClick={onClose}
      />
      <section
        ref={modalRef}
        className="dev-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        tabIndex={-1}
      >
        <header>
          <div>
            <h2 id={titleId}>{title}</h2>
            <p>{description}</p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            aria-label="Close dialog"
            data-testid={testId ? `${testId}-close` : undefined}
            onClick={onClose}
          >
            <i className="ti ti-x" aria-hidden />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function ModalActions({
  onCancel,
  submit,
  submitting,
  disabled,
  submitTestId,
  cancelTestId,
}: {
  onCancel(): void;
  submit: string;
  submitting?: boolean;
  disabled?: boolean;
  submitTestId?: string;
  cancelTestId?: string;
}) {
  return (
    <div className="dev-modal-actions">
      <button
        className="dev-button"
        type="button"
        data-testid={cancelTestId}
        onClick={onCancel}
      >
        Cancel
      </button>
      <button
        className="dev-button dev-button--primary"
        type="submit"
        data-testid={submitTestId}
        disabled={disabled || submitting}
      >
        {submitting ? "Submitting…" : submit}
      </button>
    </div>
  );
}

/** Explains how the portal composes the Glofox integrator name (AES-373). */
function IntegratorNameCallout({
  businessName,
  integratorName,
  environment,
  testId,
}: {
  businessName: string;
  integratorName: string;
  environment: KeyEnvironment;
  testId: string;
}) {
  const nameForPreview = integratorName.trim() || "[Name]";
  const preview = glofoxIntegratorDisplayName(
    businessName,
    nameForPreview,
    environment,
  );

  return (
    <div className="dev-form__info" data-testid={testId} role="note">
      <i className="ti ti-info-circle" aria-hidden />
      <span>
        <strong>Glofox integrator name.</strong> Pay attention: your business
        name is essential to create the integrator. It will be created as{" "}
        <code data-testid={`${testId}-preview`}>{preview}</code>.
      </span>
    </div>
  );
}

/** Warns that Developer Portal API keys are not Zapier credentials. */
function ZapierApiKeyCallout({ testId }: { testId: string }) {
  return (
    <div className="dev-form__info" data-testid={testId}>
      <i className="ti ti-alert-triangle" aria-hidden />
      <span>
        <strong>Zapier.</strong> An API key requested in the Developer Portal
        does not work with Zapier. To request Zapier credentials, email{" "}
        <a href="mailto:glofox.apisupport@abcfitness.com">
          glofox.apisupport@abcfitness.com
        </a>
        .
      </span>
    </div>
  );
}

function RequestIntegratorForm({
  apiPlatform,
  accessRole,
  businessName,
  hasSandboxRequest,
  hasLiveRequest,
  onCancel,
  onCreated,
}: {
  apiPlatform: ApiPlatform;
  accessRole: AccessRole | null;
  businessName: string;
  hasSandboxRequest: boolean;
  hasLiveRequest: boolean;
  onCancel(): void;
  onCreated(request: AccessRequest): void;
}) {
  const [form, setForm] = useState<IntegratorRequestFormValues>(() =>
    createInitialIntegratorRequestForm(
      hasSandboxRequest && !hasLiveRequest ? "live" : "sandbox",
    ),
  );
  const [requesterType, setRequesterType] = useState<AccessRole | null>(
    accessRole,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const liveCredentialsEnabled = useLiveCredentialsEnabled();
  const useCatalog = liveCredentialsEnabled && form.environment === "live";

  const environmentAlreadyRequested =
    (form.environment === "sandbox" && hasSandboxRequest) ||
    (form.environment === "live" && hasLiveRequest);
  const isValid =
    requesterType !== null &&
    !environmentAlreadyRequested &&
    isIntegratorRequestFormValid(form, {
      useCatalog: liveCredentialsEnabled,
    });
  const scopeTextError = useCatalog
    ? null
    : validateScopeText(getIntegratorScopeText(form));

  // Submit sits at the bottom of a scrollable modal, so the banner rendered at
  // the top of the form lands outside the visible area after a failed submit.
  useEffect(() => {
    if (!error) {
      return;
    }
    errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [error]);

  function updateForm(patch: Partial<IntegratorRequestFormValues>) {
    setForm((current) => ({ ...current, ...patch }));
    setError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await createIntegratorRequest(
        toCreateIntegratorRequestPayload(form, apiPlatform, requesterType, {
          useCatalog: liveCredentialsEnabled,
        }),
      );
      onCreated(response.request);
    } catch (err: unknown) {
      setError(getIntegratorRequestSubmitErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="dev-form"
      data-testid="developer-portal-request-form"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      {error ? (
        <div
          ref={errorRef}
          className="dev-banner dev-banner--declined"
          role="alert"
          data-testid="developer-portal-request-error"
        >
          <i className="ti ti-alert-circle" aria-hidden />
          <span>{error}</span>
        </div>
      ) : null}
      {accessRole === null ? (
        <fieldset>
          <legend>Account type</legend>
          <div className="dev-toggle">
            {ACCESS_ROLE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={requesterType === option.value ? "is-active" : ""}
                data-testid={`developer-portal-request-role-${option.value}`}
                aria-pressed={requesterType === option.value}
                onClick={() => {
                  setRequesterType(option.value);
                  setError(null);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
      <label>
        <span>Name</span>
        <input
          data-testid="developer-portal-request-name"
          value={form.integratorName}
          onChange={(event) =>
            updateForm({ integratorName: event.target.value })
          }
          placeholder="For example, BFT Member Sync"
        />
      </label>
      <fieldset>
        <legend>Environment</legend>
        <div className="dev-toggle">
          {INTEGRATOR_ENVIRONMENT_OPTIONS.map((option) => {
            const unavailable =
              (option.value === "sandbox" && hasSandboxRequest) ||
              (option.value === "live" && hasLiveRequest);
            return (
              <EnvironmentToggleButton
                key={option.value}
                value={option.value}
                label={option.label}
                pressed={form.environment === option.value}
                unavailable={unavailable}
                tooltipAlignStart={option.value === "sandbox"}
                onSelect={() => {
                  if (form.environment === option.value) {
                    return;
                  }
                  setForm(createInitialIntegratorRequestForm(option.value));
                  setError(null);
                }}
              />
            );
          })}
        </div>
      </fieldset>
      <IntegratorNameCallout
        businessName={businessName}
        integratorName={form.integratorName}
        environment={form.environment}
        testId="developer-portal-request-name-info"
      />
      <ZapierApiKeyCallout testId="developer-portal-request-zapier-info" />
      {form.environment === "live" ? (
        <>
          <label>
            <span>Access scope</span>
            <select
              data-testid="developer-portal-request-access-scope"
              value={form.accessScopeType}
              onChange={(event) =>
                updateForm({
                  accessScopeType: event.target.value as AccessScopeType,
                  namespacesText: "",
                  locationNamesText: "",
                  catalogSelections: [],
                })
              }
            >
              {ACCESS_SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {liveCredentialsEnabled ? (
            <LiveScopePicker
              testId="developer-portal-request-scope"
              mode={form.accessScopeType}
              selected={form.catalogSelections}
              onChange={(catalogSelections) => updateForm({ catalogSelections })}
            />
          ) : (
            <label>
              <span>
                {form.accessScopeType === "namespaces"
                  ? "Namespaces"
                  : "Location ID, name, or namespace"}
              </span>
              {form.accessScopeType === "namespaces" ? (
                <textarea
                  data-testid="developer-portal-request-namespace"
                  value={form.namespacesText}
                  onChange={(event) =>
                    updateForm({ namespacesText: event.target.value })
                  }
                  placeholder="One or more values separated by commas. Each value can be a namespace, a location ID, or a location name."
                  aria-invalid={scopeTextError ? true : undefined}
                />
              ) : (
                <textarea
                  data-testid="developer-portal-request-location-names"
                  value={form.locationNamesText}
                  onChange={(event) =>
                    updateForm({ locationNamesText: event.target.value })
                  }
                  placeholder="One or more values separated by commas. Each value can be a location ID, a location name, or a namespace."
                  aria-invalid={scopeTextError ? true : undefined}
                />
              )}
            </label>
          )}
          {scopeTextError ? (
            <div className="dev-form__error" role="alert">
              {scopeTextError}
            </div>
          ) : null}
        </>
      ) : (
        <div
          className="dev-form__info"
          data-testid="developer-portal-request-sandbox-info"
        >
          <i className="ti ti-flask" aria-hidden />
          Sandbox requests do not need an access scope. After approval, you can
          create test locations and request API credentials.
        </div>
      )}
      <label>
        <span>Intended use case</span>
        <textarea
          data-testid="developer-portal-request-use-case"
          value={form.intendedUseCase}
          onChange={(event) =>
            updateForm({ intendedUseCase: event.target.value })
          }
          placeholder="Describe what you plan to build."
        />
      </label>
      <ModalActions
        onCancel={onCancel}
        submit="Submit request"
        submitting={submitting}
        disabled={!isValid}
        submitTestId="developer-portal-request-submit"
        cancelTestId="developer-portal-request-cancel"
      />
    </form>
  );
}

function ContestAccessRequestForm({
  request,
  businessName,
  onCancel,
  onContested,
}: {
  request: AccessRequest;
  businessName: string;
  onCancel(): void;
  onContested(updated: AccessRequest): void;
}) {
  const [form, setForm] = useState<ContestAccessRequestFormValues>(() => {
    const initial = createContestFormFromRequest(request);
    if (request.access_scope === "namespaces") {
      return {
        ...initial,
        catalogSelections: storedNamespaceSelections(request.namespaces),
      };
    }
    if (request.access_scope !== "locations") {
      return initial;
    }
    return {
      ...initial,
      catalogSelections: storedLocationSelections(
        request.location_ids,
        request.location_names,
      ),
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const liveCredentialsEnabled = useLiveCredentialsEnabled();
  const isSandbox = form.environment === "sandbox";
  const isLive = form.environment === "live";
  const useCatalog = liveCredentialsEnabled && isLive;

  const isValid = isContestAccessRequestFormValid(form, {
    useCatalog: liveCredentialsEnabled,
  });
  const messageLen = form.contestationMessage.trim().length;
  const scopeText =
    form.accessScopeType === "namespaces"
      ? form.namespacesText
      : form.locationNamesText;
  const scopeTextError =
    isLive && !useCatalog ? validateScopeText(scopeText) : null;
  const environmentLabel = isSandbox ? "Sandbox" : isLive ? "Live" : "Legacy";

  useEffect(() => {
    if (!error) {
      return;
    }
    errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [error]);

  function updateForm(patch: Partial<ContestAccessRequestFormValues>) {
    setForm((current) => ({ ...current, ...patch }));
    setError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const updated = await contestAccessRequest(
        request.id,
        toContestAccessRequestPayload(form, {
          useCatalog: liveCredentialsEnabled,
        }),
      );
      onContested(updated);
    } catch (err: unknown) {
      setError(getContestAccessRequestSubmitErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="dev-form"
      data-testid="developer-portal-contest-form"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      {error ? (
        <div
          ref={errorRef}
          className="dev-banner dev-banner--declined"
          role="alert"
          data-testid="developer-portal-contest-error"
        >
          <i className="ti ti-alert-circle" aria-hidden />
          <span>{error}</span>
        </div>
      ) : null}

      {request.rejection_reason ? (
        <div
          className="dev-form__info"
          data-testid="developer-portal-contest-prior-reason"
        >
          <i className="ti ti-info-circle" aria-hidden />
          <span>
            <strong>Prior reason:</strong> {request.rejection_reason}
          </span>
        </div>
      ) : null}

      <label>
        <span>Environment</span>
        <input
          data-testid="developer-portal-contest-environment"
          value={environmentLabel}
          readOnly
          disabled
        />
      </label>

      <label>
        <span>Name</span>
        <input
          data-testid="developer-portal-contest-name"
          value={form.integratorName}
          onChange={(event) =>
            updateForm({ integratorName: event.target.value })
          }
        />
      </label>

      {isSandbox || isLive ? (
        <IntegratorNameCallout
          businessName={businessName}
          integratorName={form.integratorName}
          environment={isLive ? "live" : "sandbox"}
          testId="developer-portal-contest-name-info"
        />
      ) : null}

      {isLive ? (
        <>
          <label>
            <span>Access scope</span>
            <select
              data-testid="developer-portal-contest-access-scope"
              value={form.accessScopeType}
              onChange={(event) =>
                updateForm({
                  accessScopeType: event.target.value as AccessScopeType,
                  namespacesText: "",
                  locationNamesText: "",
                  catalogSelections: [],
                })
              }
            >
              {ACCESS_SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {useCatalog ? (
            <LiveScopePicker
              testId="developer-portal-contest-scope"
              mode={form.accessScopeType}
              selected={form.catalogSelections}
              onChange={(catalogSelections) => updateForm({ catalogSelections })}
            />
          ) : (
            <label>
              <span>
                {form.accessScopeType === "namespaces"
                  ? "Namespaces"
                  : "Location ID, name, or namespace"}
              </span>
              {form.accessScopeType === "namespaces" ? (
                <textarea
                  data-testid="developer-portal-contest-namespaces"
                  value={form.namespacesText}
                  onChange={(event) =>
                    updateForm({ namespacesText: event.target.value })
                  }
                  placeholder="One or more values separated by commas. Each value can be a namespace, a location ID, or a location name."
                  aria-invalid={scopeTextError ? true : undefined}
                />
              ) : (
                <textarea
                  data-testid="developer-portal-contest-location-names"
                  value={form.locationNamesText}
                  onChange={(event) =>
                    updateForm({ locationNamesText: event.target.value })
                  }
                  placeholder="One or more values separated by commas. Each value can be a location ID, a location name, or a namespace."
                  aria-invalid={scopeTextError ? true : undefined}
                />
              )}
            </label>
          )}
          {scopeTextError ? (
            <div className="dev-form__error" role="alert">
              {scopeTextError}
            </div>
          ) : null}
        </>
      ) : isSandbox ? (
        <div
          className="dev-form__info"
          data-testid="developer-portal-contest-sandbox-info"
        >
          <i className="ti ti-flask" aria-hidden />
          Sandbox requests do not need an access scope.
        </div>
      ) : null}

      <label>
        <span>Intended use case</span>
        <textarea
          data-testid="developer-portal-contest-use-case"
          value={form.intendedUseCase}
          onChange={(event) =>
            updateForm({ intendedUseCase: event.target.value })
          }
        />
      </label>

      <label>
        <span>Contestation message</span>
        <textarea
          data-testid="developer-portal-contest-message"
          value={form.contestationMessage}
          maxLength={CONTESTATION_MESSAGE_LIMITS.max}
          onChange={(event) =>
            updateForm({ contestationMessage: event.target.value })
          }
          placeholder="Explain what you corrected and why the request should be reviewed again."
        />
        <small data-testid="developer-portal-contest-message-counter">
          {messageLen}/{CONTESTATION_MESSAGE_LIMITS.max}
        </small>
      </label>

      <ModalActions
        onCancel={onCancel}
        submit="Submit contest"
        submitting={submitting}
        disabled={!isValid}
        submitTestId="developer-portal-contest-submit"
        cancelTestId="developer-portal-contest-cancel"
      />
    </form>
  );
}
